import React, { useState, useEffect } from 'react';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { saveAs } from 'file-saver';
import { withRouter } from 'react-router';
import connect from 'react-redux/lib/connect/connect';

import {
    EBON_TYPE_DOWNLOAD_CODE,
    EBON_TYPE_LINK_CODE,
    EBON_TYPE_PRE_DOWNLOADED_LINK,
    ENTERPRISE_CARD_NAME
} from '../../../../constants/ebon';
import { API_ROOT } from '../../../../constants';
import { HTTP_METHOD_GET } from '../../../../constants/httpMethod';
import { WAITING_STOCK } from '../../../../services/checkout/constants';

import { fetchAccountEbonOrderByIdWithCode } from '../../../../services/account/ebons/actions';

import { formatAmountCurrency } from '../../../../utils';
import { convertDate } from '../../../../utils/dateUtils';
import { convertHrefLink, convertHtmlToReactDomWithParams } from '../../../../utils/htmlUtils';

import EnterpriseCardSteps from './EnterpriseCardSteps';
import ButtonComponent from '../../../button/ButtonComponent';
import { LoadingComponent } from '../../../loading/LoadingComponent';

import './EbonOrderCard.scss';

const DOWNLOAD_CODE_TYPE = [EBON_TYPE_DOWNLOAD_CODE, EBON_TYPE_PRE_DOWNLOADED_LINK];

const EbonOrderCard = ({
    fetchAccountEbonOrderByIdWithCode,
    i18nPage,
    BRAND_NAME_ALT,
    items,
    mergedItems,
    id,
    vendor,
    state,
    date,
    ebonOrderWithCodes,
    token,
    paymentRequest,
    isEnterpriseCard
}) => {
    const [open, setOpen] = useState(false);
    const [showHowTo, setshowHowTo] = useState(false);
    const [fetchingCodes, setFetchingCodes] = useState(false);
    const [fetchingBill, setFetchingBill] = useState(false);
    const { ebonList } = ebonOrderWithCodes;
    const total = items.reduce(
        (acc, { stock, value }) => ({
            stock: acc.stock + stock,
            value: acc.value + value
        }),
        { stock: 0, value: 0 }
    );
    const totalRemainingBalance = items.reduce((acc, curr) => acc + curr.remainingBalance, 0);
    const stateOk = state !== WAITING_STOCK;

    const Chevron = ({ width, height, className }) => (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            className={className}
            width={width}
            height={height}
            viewBox="0 0 12 7">
            <path
                fill="currentColor"
                fillRule="evenodd"
                d="m6.195 6.995l5.814-5.22-0.985-1.775-5 4.355-5.014-4.355-1.015 1.775 5.814 5.22h0.386z"
            />
        </svg>
    );

    useEffect(() => {
        if (stateOk && open && isEmpty(ebonOrderWithCodes)) {
            fetchAccountEbonOrderByIdWithCode(id);
        }
    }, [open]);

    const getDownload = async (uri, filename) => {
        let config = {
            credentials: 'same-origin',
            method: HTTP_METHOD_GET,
            headers: {
                Authorization: `Bearer ${token}`
            }
        };
        fetch(`${API_ROOT}${uri}`, config)
            .then(response => response.blob())
            .then(blob => {
                var file = new Blob([blob], { type: 'application/pdf' });
                saveAs(file, filename);
                setFetchingCodes(false);
                setFetchingBill(false);
            });
    };

    const getDownloadCodes = async () => {
        setFetchingCodes(true);
        await getDownload(`/ebon/order/${id}/export`, `mes-ebons-${id}.pdf`);
    };

    const getDownloadBill = async () => {
        setFetchingBill(true);
        await getDownload(`/ebon/order/${id}/bill`, `ma-facture-${id}.pdf`);
    };

    const hasEbonLink = ebonListWithCodes => {
        return (
            ebonListWithCodes &&
            ebonListWithCodes.some(ebon => ebon.ebonCodeDto.type === EBON_TYPE_LINK_CODE)
        );
    };

    const getLinkCode = (ebonListWithCodes, value) => {
        let actionArray = [];
        ebonListWithCodes
            .filter(ebon => ebon.facialValue === value)
            .forEach(ebon => {
                actionArray.push({
                    label: `${i18nPage.GET_CODES} (${formatAmountCurrency(ebon.facialValue)})`,
                    extra: ebon.ebonCodeDto.pinCode,
                    action: convertHrefLink(ebon.ebonCodeDto.field4)
                });
            });
        return actionArray;
    };

    const getCodeActions = ebonListWithCodes => {
        let actionArray = [];

        if (ebonListWithCodes.some(ebon => DOWNLOAD_CODE_TYPE.includes(ebon.ebonCodeDto.type))) {
            const nbEbon = ebonListWithCodes.filter(ebon =>
                DOWNLOAD_CODE_TYPE.includes(ebon.ebonCodeDto.type)
            );
            const totalDownload = nbEbon.reduce((a, v) => (a += v.facialValue), 0);
            actionArray.push({
                label: `${
                    nbEbon.length > 1 ? i18nPage.DOWNLOAD_CODES : i18nPage.DOWNLOAD_CODE
                } (${formatAmountCurrency(totalDownload)})`,
                action: () => getDownloadCodes()
            });
        }

        return actionArray;
    };

    const renderVendorName = () => {
        return `${vendor ? vendor : ENTERPRISE_CARD_NAME} ${
            !vendor || paymentRequest ? '(' + i18nPage.PAYMENT_REQUEST + ')' : ''
        }`;
    };

    const displayOrderId =
        typeof id === 'string' && id.includes('-') ? id.substring(0, id.indexOf('-')) : id;

    return (
        <div className="order-container">
            <div className="card-title">
                {`${i18nPage.ORDER}${displayOrderId}`}{' '}
                {state === WAITING_STOCK && (
                    <span className="waiting-stock">{i18nPage.WAITING_STOCK}</span>
                )}
            </div>
            <dl className="description-list" onClick={() => setOpen(!open)}>
                <div className="description-container">
                    <dt className="description-title">{i18nPage.ORDER_DATE}</dt>
                    <dd className="description-details">{convertDate(date)}</dd>
                </div>
                <div className="description-container">
                    <dt className="description-title">{i18nPage.VENDOR}</dt>
                    <dd className="description-details">{renderVendorName()}</dd>
                </div>
                <div className="description-container">
                    <dt className="description-title">{i18nPage.ORDER_TOTAL}</dt>
                    <dd className="description-details">{formatAmountCurrency(total.value)}</dd>
                </div>
                {isEnterpriseCard && (
                    <div className="description-container">
                        <dt className="description-title">{i18nPage.ENTERPRISE_CARD_REMAINING}</dt>
                        <dd className="description-details remaining">
                            {formatAmountCurrency(totalRemainingBalance)}
                        </dd>
                    </div>
                )}
                <button onClick={() => setOpen(!open)} type="button">
                    {open ? (
                        <Chevron width={20} height={20} className="chevron top" />
                    ) : (
                        <Chevron width={20} height={20} className="chevron" />
                    )}
                </button>
            </dl>

            {open && (
                <React.Fragment>
                    <div className="tableContainer">
                        <table className="tableStyle">
                            <tbody>
                                <tr className="tableRowHeader">
                                    <td>
                                        <span className="tableHeaderStyle">
                                            {i18nPage.EBON.COMMAND}
                                        </span>
                                    </td>
                                    <td>
                                        <span className="tableHeaderStyle">
                                            {i18nPage.EBON.QUANTITY}
                                        </span>
                                    </td>
                                    <td>
                                        <span className="tableHeaderStyle">
                                            {i18nPage.EBON.AMOUNT}
                                        </span>
                                    </td>
                                    <td>
                                        <span className="tableHeaderStyle">
                                            {i18nPage.EBON.EXPIRATION}
                                        </span>
                                    </td>
                                    {hasEbonLink(ebonList) && (
                                        <td>
                                            <span className="tableHeaderStyle">
                                                {i18nPage.EBON.LINK}
                                            </span>
                                        </td>
                                    )}
                                </tr>
                                {mergedItems.map(({ id, name, stock, value, expirationDate }) => (
                                    <tr key={`ebons${id}`} className="tableRowItem">
                                        <td
                                            style={{
                                                maxWidth: '200px'
                                            }}>
                                            <span className="tdTextStyle">{name}</span>
                                        </td>
                                        <td>
                                            <span className="tdTextStyle">{stock}</span>
                                        </td>
                                        <td>
                                            <span className="tdTextStyle">
                                                {formatAmountCurrency(value)}
                                            </span>
                                        </td>
                                        <td>
                                            <span className="tdTextStyle">
                                                {convertDate(expirationDate)}
                                            </span>
                                        </td>
                                        {hasEbonLink(ebonList) && (
                                            <td>
                                                <span className="tdTextStyle ebonLink">
                                                    {getLinkCode(ebonList, value).map(
                                                        ({ label, action }, i) => (
                                                            <a
                                                                key={label}
                                                                href={action}
                                                                target="_blank">
                                                                {i18nPage.EBON.LINK_ACTION}
                                                            </a>
                                                        )
                                                    )}
                                                </span>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                                <tr className="tableRowHeader">
                                    <td>
                                        <span className="tableHeaderStyle">
                                            {i18nPage.EBON.TOTAL}
                                        </span>
                                    </td>
                                    <td>
                                        <span className="tableHeaderStyle">{total.stock}</span>
                                    </td>
                                    <td>
                                        <span className="tableHeaderStyle">
                                            {formatAmountCurrency(total.value)}
                                        </span>
                                    </td>
                                    <td>
                                        <span className="tdTextStyle">-</span>
                                    </td>
                                    {hasEbonLink(ebonList) && (
                                        <td>
                                            <span className="tdTextStyle">&nbsp;</span>
                                        </td>
                                    )}
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    {stateOk && (
                        <div className="card-button">
                            {!isEnterpriseCard && (
                                <div className="link-list">
                                    {ebonList && ebonList.length > 0 ? (
                                        getCodeActions(ebonList).map(({ label, action }, i) => (
                                            <ButtonComponent
                                                key={i}
                                                color="main"
                                                text={label}
                                                onClick={() => action()}
                                                disabled={fetchingCodes}
                                            />
                                        ))
                                    ) : (
                                        <LoadingComponent />
                                    )}
                                </div>
                            )}
                            <div>
                                {!isEnterpriseCard && vendor && !paymentRequest && (
                                    <ButtonComponent
                                        color="main"
                                        text={i18nPage.DOWNLOAD_BILL}
                                        onClick={() => getDownloadBill()}
                                        disabled={fetchingBill}
                                    />
                                )}
                            </div>
                            {isEnterpriseCard && (
                                <div className="enterprise-card-how-to-button">
                                    <span
                                        id="how-to"
                                        className="fakelink"
                                        onClick={() => setshowHowTo(!showHowTo)}>
                                        {i18nPage.ENTERPRISE_CARD_HOW_TO}
                                        <Chevron
                                            width={15}
                                            height={15}
                                            className={`chevron ${showHowTo ? 'top' : ''}`}
                                        />
                                    </span>
                                </div>
                            )}
                        </div>
                    )}

                    {isEnterpriseCard && showHowTo && (
                        <React.Fragment>
                            <div className="enterprise-card-how-to">
                                <EnterpriseCardSteps />
                            </div>
                            <div className="enterprise-card-explanation">
                                {convertHtmlToReactDomWithParams(
                                    i18nPage.ENTERPRISE_CARD_EXPLANATION,
                                    ENTERPRISE_CARD_NAME,
                                    BRAND_NAME_ALT
                                )}
                            </div>
                        </React.Fragment>
                    )}
                </React.Fragment>
            )}
        </div>
    );
};

EbonOrderCard.propTypes = {
    fetchAccountEbonOrderByIdWithCode: PropTypes.func,
    i18nPage: PropTypes.object.isRequired,
    items: PropTypes.array,
    mergedItems: PropTypes.array,
    id: PropTypes.string,
    partnerId: PropTypes.number,
    vendor: PropTypes.string,
    date: PropTypes.number
};

const mapStateToProps = (state, ownProps) => {
    return {
        i18nPage: state.i18n.page.ACCOUNT.EBONS,
        BRAND_NAME_ALT: state.i18n.common.BRAND_NAME_ALT,
        ebonOrderWithCodes: searchCodes(state.account.myebons.ebonOrderWithCodes, ownProps.id),
        token: state.authentication.token
    };
};

const searchCodes = (codes, orderId) => {
    const filteredCodes = codes.filter(code => code.id === orderId);
    return filteredCodes.length > 0 ? filteredCodes[0] : {};
};

const mapDispatchToProps = {
    fetchAccountEbonOrderByIdWithCode
};
export default withRouter(connect(mapStateToProps, mapDispatchToProps)(EbonOrderCard));



// WEBPACK FOOTER //
// ./src/components/pages/account/myebons/EbonOrderCard.js